'use client'

import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { ApiFilterResponse } from '@interfaces/ackoDrive'
import assetLoader from '@utils/assetloader'
import styles from './HeaderNavModal.module.scss'
import NewCarNavModal from '../CarNavModal'

interface HeaderNavModalProps {
  headerNavData: {
    blog: Array<{
      title: string
      href: string
      icon: string
      hasSubMenu?: boolean
    }>
    buyCar: Array<{
      title: string
      href: string
      icon: string
      hasSubMenu?: boolean
    }>
  }
  onClose: () => void
  type: 'blog' | 'buyCar'
  position: number
  onMouseEnter: () => void
  onMouseLeave: () => void
  onNewCarNavModalHover: (isHovering: boolean) => void
  filterData: ApiFilterResponse | null
}

const HeaderNavModal: React.FC<HeaderNavModalProps> = ({
  headerNavData,
  onClose,
  type,
  position,
  onMouseEnter,
  onMouseLeave,
  onNewCarNavModalHover,
  filterData,
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const subModalRef = useRef<HTMLDivElement>(null)
  const subModalTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [isNavSubModalOpen, setIsNavSubModalOpen] = useState<boolean>(false)
  const [navSubModalPosition, setNavSubModalPosition] = useState<number>(0)
  const [isMouseOverSubModal, setIsMouseOverSubModal] = useState<boolean>(false)

  const clearSubModalTimeout = () => {
    if (subModalTimeoutRef.current) {
      clearTimeout(subModalTimeoutRef.current)
      subModalTimeoutRef.current = null
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        subModalRef.current &&
        !subModalRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  const handleNavSubItemMouseEnter = (event: React.MouseEvent<HTMLAnchorElement>, item: any) => {
    clearSubModalTimeout()
    if (item?.hasSubMenu) {
      const buttonRect = event.currentTarget.getBoundingClientRect()
      setNavSubModalPosition(buttonRect.left + buttonRect.width)
      setIsNavSubModalOpen(true)
    } else {
      setIsNavSubModalOpen(false)
    }
  }

  const handleNavSubItemMouseLeave = () => {
    subModalTimeoutRef.current = setTimeout(() => {
      if (!isMouseOverSubModal) {
        setIsNavSubModalOpen(false)
      }
    }, 100)
  }

  const handleNewCarNavModalHover = (isHovering: boolean) => {
    setIsMouseOverSubModal(isHovering)
    onNewCarNavModalHover(isHovering)

    if (isHovering) {
      clearSubModalTimeout()
    } else {
      setIsNavSubModalOpen(false)
    }
  }

  const navItems = headerNavData[type]

  return (
    <>
      <div
        ref={modalRef}
        className={styles.modalContent}
        style={{ left: `${position}px` }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}>
        {navItems.map((item, index) => (
          <Link
            key={index}
            href={item.href}
            className={styles.navItem}
            onClick={onClose}
            onMouseEnter={e => handleNavSubItemMouseEnter(e, item)}
            onMouseLeave={handleNavSubItemMouseLeave}>
            <Image src={`${item.icon}`} alt={item.title} width={40} height={40} loading="lazy" />
            <p className={styles.title}>{item.title}</p>
            {filterData && item.hasSubMenu && (
              <Image
                src={`${assetLoader()}/icons/Chevron_right_light.svg`}
                alt="Arrow Right"
                width={20}
                height={20}
                className={styles.innerNavIcon}
              />
            )}
          </Link>
        ))}
      </div>
      {filterData && isNavSubModalOpen && (
        <NewCarNavModal
          position={navSubModalPosition}
          onHover={handleNewCarNavModalHover}
          onClose={onClose}
          modalRef={subModalRef}
          filterData={filterData}
        />
      )}
    </>
  )
}

export default HeaderNavModal
